import { useState, useMemo, useCallback } from 'react';

export const useDocumentFiltering = (documents) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [sortedInfo, setSortedInfo] = useState({});
  const [filteredInfo, setFilteredInfo] = useState({});
  const [dateFilter, setDateFilter] = useState(null);
  const [dateRangeFilter, setDateRangeFilter] = useState(null);

  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply global search filter
    if (globalSearch) {
      filtered = filtered.filter(doc =>
        Object.values(doc).some(value =>
          String(value).toLowerCase().includes(globalSearch.toLowerCase())
        )
      );
    }

    // Apply date filters
    if (dateFilter) {
      filtered = filtered.filter(doc => {
        const docDate = new Date(doc.uploadDate).toDateString();
        return docDate === dateFilter.toDateString();
      });
    }

    if (dateRangeFilter && dateRangeFilter.length === 2) {
      const [startDate, endDate] = dateRangeFilter;
      filtered = filtered.filter(doc => {
        const docDate = new Date(doc.uploadDate);
        return docDate >= startDate && docDate <= endDate;
      });
    }

    // Apply column filters
    Object.entries(filteredInfo).forEach(([key, value]) => {
      if (value && value.length > 0) {
        filtered = filtered.filter(doc => value.includes(doc[key]));
      }
    });

    return filtered;
  }, [documents, searchTerm, globalSearch, dateFilter, dateRangeFilter, filteredInfo]);

  const handleChange = useCallback((pagination, filters, sorter) => {
    setSortedInfo(sorter);
    setFilteredInfo(filters);
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchTerm('');
    setGlobalSearch('');
    setSelectedColumns([]);
    setSortedInfo({});
    setFilteredInfo({});
    setDateFilter(null);
    setDateRangeFilter(null);
  }, []);

  const visibleColumnNames = useMemo(() => ({
    name: true,
    company: true,
    type: true,
    uploadDate: true
  }), []);

  const debounce = useCallback((func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  }, []);

  const handleSearchChange = useMemo(
    () => debounce((e) => setSearchTerm(e.target.value), 300),
    []
  );

  return {
    searchTerm,
    setSearchTerm,
    globalSearch,
    setGlobalSearch,
    selectedColumns,
    setSelectedColumns,
    sortedInfo,
    setSortedInfo,
    filteredInfo,
    setFilteredInfo,
    dateFilter,
    setDateFilter,
    dateRangeFilter,
    setDateRangeFilter,
    filteredDocuments,
    handleChange,
    clearAllFilters,
    visibleColumnNames,
    handleSearchChange,
  };
};